import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TwoFactorAuthService, TwoFactorStatus } from '../../services/two-factor-auth.service';

type SetupStep = 'status' | 'qr' | 'verify' | 'backup' | 'disable';

@Component({
  selector: 'app-two-factor-setup',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './two-factor-setup.component.html',
  styleUrl: './two-factor-setup.component.css'
})
export class TwoFactorSetupComponent implements OnInit {
  // State
  status: TwoFactorStatus = { enabled: false, hasSecret: false };
  currentStep: SetupStep = 'status';
  isLoading: boolean = false;
  error: string = '';
  
  // Setup data
  qrCode: string = '';
  secret: string = '';
  verificationCode: string = '';
  backupCodes: string[] = [];
  
  // Disable
  disableCode: string = '';

  constructor(private twoFactorService: TwoFactorAuthService) {}

  async ngOnInit(): Promise<void> {
    await this.loadStatus();
  }

  async loadStatus(): Promise<void> {
    this.isLoading = true;
    try {
      this.status = await this.twoFactorService.getStatus();
      this.currentStep = 'status';
    } catch (error) {
      this.error = 'Failed to load 2FA status';
    } finally {
      this.isLoading = false;
    }
  }

  async startSetup(): Promise<void> {
    this.isLoading = true;
    this.error = '';
    
    try {
      const result = await this.twoFactorService.startSetup();
      
      if (result.success && result.qrCode && result.secret) {
        this.qrCode = result.qrCode;
        this.secret = result.secret;
        this.currentStep = 'qr';
      } else {
        this.error = result.message || 'Failed to start 2FA setup';
      }
    } catch (error) {
      this.error = 'Failed to start 2FA setup';
    } finally {
      this.isLoading = false;
    }
  }

  async verifySetup(): Promise<void> {
    if (!this.verificationCode || this.verificationCode.length !== 6) {
      this.error = 'Please enter a valid 6-digit code';
      return;
    }

    this.isLoading = true;
    this.error = '';
    
    try {
      const result = await this.twoFactorService.verifySetup(this.verificationCode);
      
      if (result.success && result.backupCodes) {
        this.backupCodes = result.backupCodes;
        this.currentStep = 'backup';
        this.status.enabled = true;
      } else {
        this.error = result.message || 'Invalid verification code';
        this.verificationCode = '';
      }
    } catch (error) {
      this.error = 'Failed to verify code';
    } finally {
      this.isLoading = false;
    }
  }

  async disable2FA(): Promise<void> {
    if (!this.disableCode || this.disableCode.length !== 6) {
      this.error = 'Please enter your current 2FA code';
      return;
    }

    this.isLoading = true;
    this.error = '';
    
    try {
      const result = await this.twoFactorService.disable(this.disableCode);
      
      if (result.success) {
        this.status.enabled = false;
        this.status.hasSecret = false;
        this.currentStep = 'status';
        this.disableCode = '';
        this.resetSetupData();
      } else {
        this.error = result.message || 'Failed to disable 2FA';
        this.disableCode = '';
      }
    } catch (error) {
      this.error = 'Failed to disable 2FA';
    } finally {
      this.isLoading = false;
    }
  }

  copySecret(): void {
    navigator.clipboard.writeText(this.secret);
  }

  copyBackupCodes(): void {
    const codesText = this.backupCodes.join('\n');
    navigator.clipboard.writeText(codesText);
  }

  downloadBackupCodes(): void {
    const codesText = `Meetzy Admin 2FA Backup Codes\n${'='.repeat(40)}\n\nSave these codes in a safe place.\nEach code can only be used once.\n\n${this.backupCodes.join('\n')}\n\nGenerated: ${new Date().toISOString()}`;
    
    const blob = new Blob([codesText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'meetzy-2fa-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  }

  finishSetup(): void {
    this.resetSetupData();
    this.currentStep = 'status';
  }

  cancelSetup(): void {
    this.resetSetupData();
    this.currentStep = 'status';
  }

  showDisableForm(): void {
    this.currentStep = 'disable';
    this.disableCode = '';
    this.error = '';
  }

  cancelDisable(): void {
    this.currentStep = 'status';
    this.disableCode = '';
    this.error = '';
  }

  onCodeInput(event: any, field: 'verificationCode' | 'disableCode'): void {
    const value = event.target.value.replace(/[^0-9]/g, '').substring(0, 6);
    if (field === 'verificationCode') {
      this.verificationCode = value;
    } else {
      this.disableCode = value;
    }
    this.error = '';
  }

  private resetSetupData(): void {
    this.qrCode = '';
    this.secret = '';
    this.verificationCode = '';
    this.backupCodes = [];
    this.error = '';
  }
}














